/**
 * /mcp/authorize?request=<ID> — AI エージェント（MCP のクライアント）の接続を許可する画面（2026-09-28 creator/mcp-dashboard-login・founder 優先度 高）。
 *
 * WHY ダッシュボードで: MCP のオリジンのログインのフォームでは、ダッシュボードで保存したパスワードが Chrome に出なかった。ここなら
 *   保存したパスワード・パスキー・Google / GitHub のログインがそのまま使え、ログイン済みなら同意だけで済む。
 *   ログインしていなければ App.tsx のログイン前の木が /login へ送り、ログイン後に `state.from`（パス + クエリ）でここへ戻る（今の return path の仕組み）。
 *
 * 流れ（全体は apps/mcp/src/auth/dashboardLogin.ts の冒頭）:
 *   MCP の Worker が本体に要求を登録 → ブラウザがここへ（URL には要求 ID だけ）→ kds_ で本体から要求・自分のプロジェクト・メールの確認を読む
 *   （mcpAuthorizeApi.ts・型の正は docs/teams/creator/tasks/mcp-grants-api.md）
 *   → 本人がスコープとプロジェクトを選んで「許可する」/「キャンセル」→ 本体が返した URL（MCP のオリジンの決まったパス + code + state）へ移る。
 *   **ダッシュボードは戻り先を組み立てない**（本体が返したものを http(s) か確かめて移るだけ・mcpAuthorize.ts safeNextUrl）。
 *
 * 守り:
 *   - 画面に出す中身（名前・戻り先・スコープ）は本体から読んだものだけ。URL の `request` 以外のパラメータは読まない（readRequestId）
 *   - クライアントの名前は自己申告 = 文言に混ぜず、別の要素（<strong>）でテキストとして出す。3 つの書き分けは identityKind
 *   - 選び方 3 つ（作って公開する〔既定〕・見るだけ・自分で選ぶ）。出金・メールの送信・BAN はどの選び方にも入れない（mcpAuthorize.ts PRESET_SCOPES の WHY）
 *   - クリックジャッキング: public/_headers の frame-ancestors 'none' / X-Frame-Options: DENY が全ページに効く（消さない）
 *   - メール未確認は本体が断る（kdt_ を作らない）。ここでは先に案内を出す（emailVerified === false）。本体の 403 email_unverified も同じ案内
 *   - 再認証が要ると本体が言ったら（400 reauthentication_required）、今の再認証のダイアログ（components/Reauth.tsx）
 */
import { useMemo, useRef, useState, type FormEvent } from 'react';
import { useLocation } from 'react-router-dom';
import type { ApiError } from '../api';
import { EmailUnverifiedNotice } from '../components/EmailUnverified';
import { useReauth } from '../components/Reauth';
import { Spinner } from '../components/ui';
import { useResource } from '../hooks';
import { errorText, useT, type MessageKey } from '../i18n';
import { isLaunchHidden } from '../launchHidden';
import {
  approvalBody,
  displayName,
  identityKind,
  KNOWN_WEB_APPS,
  knownWebAppName,
  initialPreset,
  presetNoteKey,
  presetScopes,
  PRESET_ORDER,
  readRequestId,
  safeNextUrl,
  scopeMessageKey,
  visibleScopes,
  type KnownWebAppId,
  type McpAuthRequestResponse,
  type McpScope,
  type ScopePreset,
  type ProjectsMode
} from '../mcpAuthorize';
import { approveMcpAuthRequest, denyMcpAuthRequest, mcpAuthRequestPath } from '../mcpAuthorizeApi';
import { initialProjects, projectsSummary } from '../mcpAuthorizeProjects';
import { consentState, simpleConsentFor } from '../mcpAuthorizeSimple';
import { appHomeUrl, backToChat, goneKnownApp, stepUpStart, stepUpView, type PlainPart } from '../mcpAuthorizeStepUp';
import { useSession } from '../session';
import { AuthShell } from './AccountRecovery';

/** 要求が無い・期限切れ・使用済み（本体はどれも 404 not_found で返す・mcp-grants-api.md ③）。410 は念のため同じ扱い */
const isGone = (e: ApiError | null) => !!e && (e.status === 404 || e.status === 410);

export function McpAuthorizePage() {
  const { t } = useT();
  const location = useLocation();
  const { developer } = useSession();
  const id = readRequestId(location.search);
  const req = useResource<McpAuthRequestResponse>(id ? mcpAuthRequestPath(id) : null);

  if (!id) return <Message text={t('mcpAuth.error.invalidLink')} />;
  if (req.loading && !req.data) {
    return (
      <AuthShell title={t('mcpAuth.pageTitle')}>
        <Spinner label={t('common.loading')} />
      </AuthShell>
    );
  }
  if (isGone(req.error)) return <Expired app={goneKnownApp(req.error)} />;
  if (req.error || !req.data) return <Message text={req.error ? errorText(req.error) : t('mcpAuth.error.expired')} />;
  // メール未確認: 本体は承認を 403 email_unverified で断る（kdt_ を作らない・SEC-DEVLOGIN-01）。押してから断られるより先に案内する。
  // 本体の応答の developer を正にし、無ければセッションの値（古い応答の念のため）
  const verified = req.data.developer?.emailVerified ?? developer?.emailVerified;
  if (verified === false) {
    return (
      <AuthShell title={t('mcpAuth.pageTitle')}>
        <EmailUnverifiedNotice>{t('mcpAuth.emailUnverified', { email: req.data.developer?.email ?? developer?.email ?? '' })}</EmailUnverifiedNotice>
      </AuthShell>
    );
  }
  return <ConsentForm id={id} data={req.data} />;
}

/**
 * 期限切れ・使用済み（2026-10-05 stepup-ux C・mcpAuthorizeStepUp.ts の冒頭）。Web の ChatGPT / Claude から来た要求なら
 * 「チャットに戻って、もう一度押す」と「Back to ChatGPT / Claude」のボタン。それ以外（手元のアプリ・本体が教えない古い要求）は今までの文。
 * ボタン = backToChat（開いた窓なら閉じる → 履歴で戻る）。経緯と順番の WHY は mcpAuthorizeStepUp.ts backToChat（追記 1: ホームへのリンクは
 *   新しいチャットに迷い込んだ・追記 2: Close this tab は同じタブで開いたときに効かなかった）。
 * 戻れなかったとき（新しいタブで履歴が無い など）だけ、案内（別のタブ・左の一覧から元のチャット）とアプリのホームへのリンクを出す
 */
function Expired({ app }: { app: KnownWebAppId | null }) {
  const { t } = useT();
  const [stuck, setStuck] = useState(false);
  if (!app) return <Message text={t('mcpAuth.error.expired')} />;
  const name = KNOWN_WEB_APPS[app].name;
  return (
    <AuthShell title={t('mcpAuth.pageTitle')}>
      <p className="alert alert-error" role="alert">
        {t(`mcpAuth.expired.${app}` as MessageKey)}
      </p>
      <p>
        <button type="button" className="btn btn-primary" onClick={() => backToChat(window, () => setStuck(true))}>
          {t('mcpAuth.backTo', { app: name })}
        </button>
      </p>
      {stuck && (
        <>
          <p className="muted" role="status">
            {t('mcpAuth.backManual', { app: name })}
          </p>
          <p>
            <a className="btn" href={appHomeUrl(app)}>
              {t('mcpAuth.openApp', { app: name })}
            </a>
          </p>
        </>
      )}
    </AuthShell>
  );
}

function Message({ text }: { text: string }) {
  const { t } = useT();
  return (
    <AuthShell title={t('mcpAuth.pageTitle')}>
      <p className="alert alert-error" role="alert">
        {text}
      </p>
    </AuthShell>
  );
}

function ConsentForm({ id, data }: { id: string; data: McpAuthRequestResponse }) {
  const { t, tr } = useT();
  const { developer, signOut } = useSession();
  const reauth = useReauth();
  const request = data.request;
  const projects = data.projects ?? [];
  const allowed = request.allowedScopes ?? [];
  // 追加の許可（今の許可がある）では「今ある許可 ∪ 求められた許可」から始める（adding-fix・mcpAuthorizeStepUp.ts stepUpStart）。
  //   以前は初めての接続と同じ initialPreset だった。初めての接続は今までどおり
  const [start] = useState(() => stepUpStart(request.existing, allowed));
  const [preset, setPreset] = useState<ScopePreset>(() => start?.preset ?? initialPreset(allowed));
  // 欄（自分で選ぶ）のチェック。最初は既定の選び方のまま = 「自分で選ぶ」に切り替えたとき、今の選び方から足し引きできる
  const [scopes, setScopes] = useState<Set<McpScope>>(() => new Set(start?.scopes ?? presetScopes(initialPreset(allowed), allowed) ?? []));
  // 検証用の MCP（request.stage・2026-10-08 creator/mcp-staging）は「すべてのプロジェクト」を選べない（本体が 403）= 最初から「選んだものだけ」
  const staging = request.stage === 'staging';
  // 開いた形（request.stagingOpen・2026-10-15 core/staging-open）: アカウントがあれば誰でも・本人のプロジェクト = ふつうの MCP と同じ選び方（「すべて」も出す）。
  //   閉じた形の検証用だけが「テスト用のプロジェクトだけ」に絞る
  const stagingOpen = staging && request.stagingOpen === true;
  const restrictedProjects = staging && !stagingOpen;
  // 最初に選んでおくプロジェクト（2026-10-14 consent-one-button・founder「ボタン押すだけにしたい」・mcpAuthorizeProjects.ts の冒頭）:
  //   追加の許可 = 今の接続のプロジェクトのまま（広げない）・初めて = 1 つならそれ、0 個 / 2 つ以上なら「すべて」・検証用 = 許されたものだけ。
  //   以前は検証用で何も選ばれていない形から始まり、閉じた「詳細」を開かないと Allow が「Choose at least one project…」で断られた
  const [projectsStart] = useState(() => initialProjects(request.existing, projects, restrictedProjects));
  const [mode, setMode] = useState<ProjectsMode>(projectsStart.mode);
  const [picked, setPicked] = useState<Set<string>>(() => new Set(projectsStart.picked));
  // 「（変える）」で詳細を開き、プロジェクトの欄まで動かす
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const projectsRef = useRef<HTMLFieldSetElement>(null);
  const openProjects = () => {
    if (detailsRef.current) detailsRef.current.open = true;
    projectsRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  };
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unverified, setUnverified] = useState(false);
  const [leaving, setLeaving] = useState(false);
  // 押した後に期限切れ・使用済みと分かった（404）。undefined = まだ。値 = 戻るアプリ（分からなければ null = 今までの文）
  const [gone, setGone] = useState<KnownWebAppId | null | undefined>(undefined);

  const name = displayName(request.client?.name);
  const host = request.redirect?.host ?? '';
  const kind = identityKind(request);
  // Web の Claude・ChatGPT（known_web・2026-10-04 core/known-web）は、自己申告の名前ではなく一覧の名前（Claude / ChatGPT）で出す
  //   = 本体が戻り先を公式の一覧と完全一致で確かめ、画面もホストを確かめた（identityKind）ときだけ
  const appName = knownWebAppName(request);
  const nameNode = <strong>{appName ?? name ?? t('mcpAuth.unnamed')}</strong>;
  const hostNode = <code>{host}</code>;
  const owned = useMemo(() => projects.map((p) => p.id), [projects]);
  const email = data.developer?.email ?? developer?.email ?? '';
  // B（2026-10-05 stepup-ux）: 同じアプリに今の許可があれば「Add permission」。今選んでいる許可で「足すこと」が変わる
  const add = stepUpView(request.existing, presetScopes(preset, allowed) ?? [...scopes]);
  const partList = (parts: PlainPart[]) => parts.map((p) => t(`mcpAuth.part.${p}` as MessageKey)).join(t('mcpAuth.listSep'));
  // consent-simplify（2026-10-24 founder・mcpAuthorizeSimple.ts）: 状態ごとに要る物だけ（初めて / もう全部ある / 足す）。
  //   今は検証用の MCP の接続だけ（simpleConsentFor）= 公開の MCP の接続は下の前の形のまま（一覧の審査中の固定）
  const simple = simpleConsentFor(request.stage);
  // 新しい形の文の中に並べる（最後の 1 つの前は「and」= 見出し・1 行が英語の文として読めるように）
  const partSentence = (parts: PlainPart[]) => {
    const words = parts.map((p) => t(`mcpAuth.part.${p}` as MessageKey));
    return words.length < 2 ? words.join('') : words.slice(0, -1).join(t('mcpAuth.listSep')) + t('mcpAuth.simple.listLast') + words[words.length - 1];
  };
  const state = consentState(add);
  // ボタンの上の 1 行（対象: …）。名前は利用者が付けたもの = 文言に混ぜず、つないだ文字として出す（{list} に入れる）
  const summary = projectsSummary(mode, picked, projects);
  const targetList =
    summary.kind === 'all' ? t('mcpAuth.projects.all')
    : summary.kind === 'none' ? t('mcpAuth.target.none')
    : summary.more > 0 ? t('mcpAuth.target.more', { names: summary.names.join(t('mcpAuth.listSep')), count: String(summary.more) })
    : summary.names.join(t('mcpAuth.listSep'));

  const toggle = <T,>(set: Set<T>, v: T, on: boolean) => {
    const next = new Set(set);
    if (on) next.add(v);
    else next.delete(v);
    return next;
  };

  /**
   * 本体が返した URL へ移る（ダッシュボードは組み立てない）。http(s) 以外なら移らない。
   * replace（assign でなく）: この画面（使い終わった許可の要求）を履歴に残さない。MCP の期限切れの画面から 1 つ戻るとチャットになる
   * （stepup-ux 追記 3・mcpAuthorizeStepUp.ts backToChat ②）。許可の後にブラウザの「戻る」で使用済みの画面に戻ることもなくなる
   */
  const go = (url: unknown) => {
    const next = safeNextUrl(url);
    if (!next) {
      setError(t('mcpAuth.error.badRedirect'));
      return;
    }
    setLeaving(true);
    window.location.replace(next);
  };

  const fail = (err: unknown) => {
    const e = err as ApiError;
    if (e?.code === 'email_unverified') setUnverified(true);
    // 期限切れ・使用済み: 本体が教えたアプリ、無ければこの画面のアプリ（Web の Claude・ChatGPT と確かめたときだけ）へ戻る画面
    else if (isGone(e)) setGone(goneKnownApp(e) ?? (kind === 'known_web' ? (request.knownApp ?? null) : null));
    // この画面の言葉に読み替える（error.account_closed の既定の文は「プロジェクトを作れない」の話で、ここでは合わない）
    else setError(errorText(e, { account_closed: 'mcpAuth.error.accountClosed', scope_not_requested: 'mcpAuth.error.scopeNotRequested' }));
  };

  const approve = async (ev: FormEvent) => {
    ev.preventDefault();
    setError(null);
    const chosen = presetScopes(preset, allowed) ?? [...scopes];
    const built = approvalBody(chosen, mode, [...picked], owned, allowed);
    if (!built.ok) {
      setError(t(`mcpAuth.error.${built.error}` as MessageKey));
      // 選ぶ所は詳細の中 = 断ったら開いて見せる（検証用でプロジェクトが無い等、初めの状態でも選べないときの念のため）
      if (built.error === 'noProject') openProjects();
      return;
    }
    setBusy(true);
    try {
      // 再認証（400 reauthentication_required）が要れば共通のダイアログで確かめて送り直す。閉じたら null（何もしない）
      const r = await reauth.run((proof) => approveMcpAuthRequest(id, built.body, proof as Record<string, string>), { action: `mcp:approve:${id}` });
      if (r) go(r.redirectUrl);
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const deny = async () => {
    setError(null);
    setBusy(true);
    try {
      go((await denyMcpAuthRequest(id)).redirectUrl);
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  if (gone !== undefined) return <Expired app={gone} />;

  if (leaving) {
    return (
      <AuthShell title={t('mcpAuth.pageTitle')}>
        <p role="status">{t('mcpAuth.returning')}</p>
      </AuthShell>
    );
  }

  // 並び（2026-10-03 console/plain-confirm・founder「今何しているかわかりずらすぎる」）: 見出し 1 文（{name} をつなぐか）→ 結果 1 行
  // （選び方ごと・mcpAuth.result.*）→ 確かめられないアプリの強い警告（インターネット上のサーバーへ送る時だけ = 見える所に残す）→ ボタン 2 つ →
  // 安全の 1 行 → ログイン中（小さく）→「詳細」（許可する操作・プロジェクト・戻り先・身元の説明）。
  // フォームの中身・送る値（approvalBody）・再認証・戻り先の検査（safeNextUrl）は以前のまま。ボタンは <form> の中 = 「詳細」の選択がそのまま送られる
  return (
    <AuthShell
      title={
        simple && state === 'already' ? tr('mcpAuth.simple.alreadyTitle', { name: nameNode })
        : simple && state === 'more' && add ? tr('mcpAuth.simple.moreTitle', { name: nameNode, list: partSentence(add.adding) })
        : add ? tr('mcpAuth.titleAdd', { name: nameNode })
        : kind === 'known_web' ? tr('mcpAuth.titleKnownWeb', { app: nameNode, host: hostNode })
        : tr('mcpAuth.title', { name: nameNode })
      }
    >
      {/* 検証用の MCP からの要求: 公開の接続と取り違えないよう、何より先に出す（2026-10-08 creator/mcp-staging） */}
      {/* 新しい形: 名前（KUMODeck Preview (v0.2)）だけを小さく先頭に。説明の段落は「Details」の先頭へ（founder「なんか色々出てくるな」） */}
      {staging && simple && (
        <p className="small" role="note">
          <span className="pill pill-warn">{t(stagingOpen ? 'mcpAuth.stagingOpen.badge' : 'mcpAuth.staging.badge')}</span>
        </p>
      )}
      {staging && !simple && (
        <div className="alert alert-warn" role="note">
          <p>
            {/* 開いた形は「テスト専用」ではない = 先行版の文（2026-10-15 core/staging-open・指示書の 2） */}
            <strong>{t(stagingOpen ? 'mcpAuth.stagingOpen.badge' : 'mcpAuth.staging.badge')}</strong> {t(stagingOpen ? 'mcpAuth.stagingOpen.note' : 'mcpAuth.staging.note')}
          </p>
        </div>
      )}
      {/* B: 今の許可がある = 追加の許可（ChatGPT は「再接続」と出す）。切れていないこと・今できること・足すことを先に言う */}
      {/* 新しい形: もう全部ある = 1 行（「Add permission」「Now: …」「already has this permission」を並べない）・足す = 今できることの 1 行 */}
      {simple && state === 'already' && <p>{tr('mcpAuth.simple.alreadyLead', { name: nameNode })}</p>}
      {simple && state === 'more' && add && <p>{t('mcpAuth.simple.moreLead', { list: partSentence(add.now) })}</p>}
      {add && !simple && (
        <div className="mcp-auth-add">
          <p>{tr('mcpAuth.addLead', { name: nameNode })}</p>
          <p className="small">{t('mcpAuth.addNow', { list: partList(add.now) })}</p>
          {/* 足すものが無い（求められた許可はもうある）= 「Adding: nothing new」ではなく、押せば続くことを言う（adding-fix・2026-10-05 founder） */}
          <p className="small">
            <strong>{add.adding.length ? t('mcpAuth.addAdding', { list: partList(add.adding) }) : tr('mcpAuth.addAlready', { name: nameNode })}</strong>
          </p>
        </div>
      )}
      {/* 「Once connected, …」は初めての接続の文 = もうつながっている（already / more）の新しい形では出さない */}
      {(!simple || state === 'first') && <p className="confirm-plain-result">{tr(`mcpAuth.result.${preset}` as MessageKey, { name: nameNode })}</p>}
      {/* 届く先（known_web）: 強い警告の代わりに、見える所へ落ち着いた 1 行（「詳細」に隠さない = 何が起きるかがすぐ分かる） */}
      {kind === 'known_web' && <p className="muted">{tr('mcpAuth.identity.knownWeb', { app: nameNode, host: hostNode })}</p>}
      {/* 新しい形の確かめられないアプリ: 初めての接続だけ強い警告（2 文 + 行き先）。もうつながっている（同じ client_id の今の接続がある =
          前に同じ警告を見て許可した同じアプリ・mcpAuthorizeSimple.ts の冒頭）なら行き先の 1 行だけ。行き先はどの状態でも見える所に残す */}
      {kind === 'remote' && simple && state === 'first' && (
        <div className="alert alert-warn" role="note">
          <p>
            <strong>{t('mcpAuth.identity.remoteTitle')}</strong> {tr('mcpAuth.simple.remote', { name: nameNode })}
          </p>
          <p>{tr('mcpAuth.simple.target', { host: hostNode })}</p>
        </div>
      )}
      {kind === 'remote' && simple && state !== 'first' && <p className="small muted">{tr('mcpAuth.simple.target', { host: hostNode })}</p>}
      {kind === 'remote' && !simple && (
        <div className="alert alert-warn" role="note">
          <p>
            <strong>{t('mcpAuth.identity.remoteTitle')}</strong> {tr('mcpAuth.identity.remote', { name: nameNode })}
          </p>
          <p>
            <strong>{tr('mcpAuth.identity.remoteTarget', { host: hostNode })}</strong>
          </p>
        </div>
      )}
      {unverified && <EmailUnverifiedNotice>{t('mcpAuth.emailUnverified', { email })}</EmailUnverifiedNotice>}
      {error && (
        <p className="alert alert-error" role="alert">
          {error}
        </p>
      )}
      <form className="confirm-plain-form" onSubmit={approve}>
        {/* 何が選ばれているかをボタンの上に 1 行で（2026-10-14 consent-one-button）。変えたい人だけ「変える」で詳細のプロジェクトの欄へ */}
        <p className="small mcp-auth-target">
          {t('mcpAuth.target', { list: targetList })}{' '}
          <button type="button" className="link-button" onClick={openProjects}>
            {t('mcpAuth.target.change')}
          </button>
        </p>
        <div className="confirm-plain-actions">
          <button type="submit" className="btn btn-primary confirm-plain-btn" disabled={busy}>
            {/* 追加の許可ではもう連携している = 「Connect」ではなく「Allow」（mcpAuth.addAlready の「Press Allow」と同じ名前） */}
            {/* 新しい形でもう全部ある = 押しても許可は増えない = 「Continue」（mcpAuth.simple.alreadyLead の「Press Continue」と同じ名前） */}
            {busy ? t('common.working') : t(simple && state === 'already' ? 'mcpAuth.simple.continue' : add ? 'mcpAuth.allowAdd' : 'mcpAuth.allow')}
          </button>
          <button type="button" className="btn confirm-plain-btn" disabled={busy} onClick={() => void deny()}>
            {t('mcpAuth.cancel')}
          </button>
        </div>
        <p className="confirm-plain-safety" role="note">
          <strong>{t('mcpAuth.safety')}</strong>
        </p>
        <p className="small muted">
          {tr('mcpAuth.signedInAs', { email: <strong>{email}</strong> })}{' '}
          <button type="button" className="link-button" onClick={() => void signOut()}>
            {t('mcpAuth.notYou')}
          </button>
        </p>
        <details className="confirm-plain-details" ref={detailsRef}>
          <summary>{t('confirmPlain.details')}</summary>
          {/* 新しい形: 検証用の MCP の説明の段落は、見える所から「Details」の先頭へ移した（名前は上の小さな印に残る） */}
          {staging && simple && <p className="small muted">{t(stagingOpen ? 'mcpAuth.stagingOpen.note' : 'mcpAuth.staging.note')}</p>}
          {kind === 'verified' && (
            <>
              <p className="muted">{tr('mcpAuth.identity.verified', { domain: <code>{request.client.verifiedDomain}</code> })}</p>
              <p className="small muted">{t('mcpAuth.identity.verifiedNote')}</p>
            </>
          )}
          {kind === 'loopback' && (
            <p className="mcp-auth-note" role="note">
              {tr('mcpAuth.identity.loopback', { name: nameNode, host: hostNode })}
            </p>
          )}
          <p className="muted">{t('mcpAuth.lead')}</p>
          <fieldset className="mcp-auth-group">
            <legend>{t('mcpAuth.permissions')}</legend>
            {PRESET_ORDER.map((p) => (
              <label key={p} className="mcp-auth-row">
                <input
                  type="radio"
                  name="mcp-preset"
                  checked={preset === p}
                  onChange={() => {
                    setPreset(p);
                    // 選び方を変えたら欄もその中身に（「自分で選ぶ」へは今のチェックのまま移る）
                    const next = presetScopes(p, allowed);
                    if (next) setScopes(new Set(next));
                  }}
                />
                <span>
                  {t(`mcpAuth.preset.${p}` as MessageKey)}
                  <small className="muted">{t(presetNoteKey(p) as MessageKey)}</small>
                </span>
              </label>
            ))}
          </fieldset>
          {preset === 'custom' && (
          <fieldset className="mcp-auth-group mcp-auth-custom">
            <legend>{t('mcpAuth.customLegend')}</legend>
            {visibleScopes(allowed).map(({ scope, danger }) => (
              <label key={scope} className="mcp-auth-row">
                <input type="checkbox" checked={scopes.has(scope)} onChange={(e) => setScopes((s) => toggle(s, scope, e.target.checked))} />
                <span>
                  {t(scopeMessageKey(scope, 'title') as MessageKey)}
                  {danger !== 'low' && <span className={`pill ${danger === 'high' ? 'pill-danger' : 'pill-warn'} mcp-auth-tag`}>{t(`mcpAuth.tag.${danger}` as MessageKey)}</span>}
                  <small className="muted">{t(scopeMessageKey(scope, 'desc') as MessageKey)}</small>
                </span>
              </label>
            ))}
          </fieldset>
          )}
          <fieldset className="mcp-auth-group" ref={projectsRef}>
            <legend>{t('mcpAuth.projects')}</legend>
            {/* 閉じた形の検証用の MCP では出さない（上の staging の WHY）。開いた形はふつうの MCP と同じに出す */}
            {!restrictedProjects && (
              <label className="mcp-auth-row">
                <input type="radio" name="mcp-projects" checked={mode === 'all'} onChange={() => setMode('all')} />
                <span>
                  {t('mcpAuth.projects.all')}
                  <small className="muted">{t('mcpAuth.projects.allNote')}</small>
                </span>
              </label>
            )}
            <label className="mcp-auth-row">
              <input type="radio" name="mcp-projects" checked={mode === 'selected'} onChange={() => setMode('selected')} />
              <span>{t('mcpAuth.projects.selected')}</span>
            </label>
            {projects.length === 0 && <p className="small muted">{t('mcpAuth.projects.none')}</p>}
            {projects.map((p) => (
              <label key={p.id} className="mcp-auth-row mcp-auth-project">
                <input
                  type="checkbox"
                  checked={picked.has(p.id)}
                  onChange={(e) => {
                    setPicked((s) => toggle(s, p.id, e.target.checked));
                    // プロジェクトにチェックを入れたら「選んだものだけ」に切り替える（チェックしたのに全部に許可、を防ぐ）
                    if (e.target.checked) setMode('selected');
                  }}
                />
                <span>
                  {p.name}
                  <small className="muted">{p.slug}</small>
                </span>
              </label>
            ))}
          </fieldset>
          {/* 出金（payouts）を閉じている間は出金の注意を出さない（2026-09-30 console/words-pass。出金の申請の許可も選べない = launchHiddenScopes） */}
          {!isLaunchHidden('payouts') && <p className="small muted">{t('mcpAuth.withdrawalNote')}</p>}
          <p className="small muted">{tr('mcpAuth.returnsTo', { host: hostNode })}</p>
        </details>
      </form>
      {reauth.dialog}
    </AuthShell>
  );
}
